import axios from "axios";
import { useEffect, useState } from "react";

import { ApiConnectionString } from "../../constants/ApiConntection";
import { useAuthContext } from "../../hooks/useAuthContext";
import LoadingSpinner from "../../components/LoadingSpinner";

import ProfileSettings from "./components/ProfileSettings";
import AppointmentSettings from "./components/AppointmentSettings";
import AvailabilitySettings from "./components/AvailabilitySettings";
import HolidaySettings from "./components/HolidaySettings";
import BookingRulesSettings from "./components/BookingRulesSettings";
import PublicAvailabilitySettings from "./components/PublicAvailabilitySettings";
import PrivacyPinSettings from "./components/PrivacyPinSettings";
import AccountSettings from "./components/AccountSettings";
import DangerZone from "./components/DangerZone";

import { getErrorMessage } from "./settingsUtils";
import "./SettingsPage.css";

export default function SettingsPage() {
  const { user } = useAuthContext();

  const [profile, setProfile] = useState(null);

  const [publicAvailability, setPublicAvailability] = useState({
    enabled: false,
    slug: "",
    url: "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const getSettings = async () => {
      if (!user?.token) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setLoadError("");

        const [profileResponse, publicAvailabilityResponse] =
          await Promise.all([
            axios.get(`${ApiConnectionString}/api/myProfile`, {
              headers: {
                Authorization: `Bearer ${user.token}`,
              },
            }),

            axios.get(
              `${ApiConnectionString}/api/therapist/settings/public-availability`,
              {
                headers: {
                  Authorization: `Bearer ${user.token}`,
                },
              }
            ),
          ]);

        const profileData = profileResponse.data || {};
        const publicData = publicAvailabilityResponse.data || {};

        const slug =
          publicData.slug ||
          publicData.Slug ||
          profileData.slug ||
          profileData.Slug ||
          "";

        setProfile(profileData);

        setPublicAvailability({
          enabled:
            publicData.publicAvailabilityEnabled ??
            publicData.PublicAvailabilityEnabled ??
            false,

          slug,

          url:
            publicData.publicAvailabilityUrl ||
            publicData.PublicAvailabilityUrl ||
            `/availability/${slug}`,
        });
      } catch (error) {
        console.error(error);

        setLoadError(
          getErrorMessage(
            error,
            "Failed to load your settings."
          )
        );
      } finally {
        setIsLoading(false);
      }
    };

    getSettings();
  }, [user?.token]);

  if (isLoading) {
    return (
      <div className="settings-page">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>
          Manage your profile, preferences and account settings.
        </p>
      </div>

      {loadError && (
        <div className="alert alert-danger">
          {loadError}
        </div>
      )}

      {profile && (
        <>
          <ProfileSettings
            profile={profile}
            token={user?.token}
            onProfileUpdated={setProfile}
          />

          <AppointmentSettings
            profile={profile}
            token={user?.token}
            onProfileUpdated={setProfile}
          />
        </>
      )}

      <AvailabilitySettings token={user?.token} />

      <HolidaySettings token={user?.token} />

      <BookingRulesSettings token={user?.token} />

      <PublicAvailabilitySettings
        publicAvailability={publicAvailability}
        token={user?.token}
        profile={profile}
        onPublicAvailabilityUpdated={setPublicAvailability}
      />

      <PrivacyPinSettings token={user?.token} />

      <AccountSettings token={user?.token} />

      <DangerZone />
    </div>
  );
}
